/**
 * Client-side price formatting, so money reads the same everywhere in the app.
 *
 * Two decimal places always — a price list where one row shows 1.5 and the next 1.4975 is hard to
 * scan and hard to trust. Stored prices may carry more precision than this (the column is
 * DECIMAL(18,4)); that precision is preserved on the way in and out, this only governs display.
 */

/** Shown in place of a price that isn't set, so a column of prices keeps its alignment. */
export const PRICE_PLACEHOLDER = '—';

export const PRICE_DECIMAL_PLACES = 2;

export const DEFAULT_CURRENCY_SYMBOL = '$';

/** Accepts the numbers the API returns and the strings the import grid holds mid-edit. */
export function parsePrice(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const trimmed = value.trim();
  if (trimmed === '') return null;

  // Tolerates a leading symbol and thousands separators, which is what a pasted CSV cell looks like.
  const cleaned = trimmed.replace(/[^0-9.\-]/g, '');

  // Stripping can empty the string entirely (e.g. 'abc'), and Number('') is 0 — so a junk cell would
  // otherwise display as a real price of zero rather than as "not set".
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;

  const numeric = Number(cleaned);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Formats a price for display, e.g. `$1,234.50`. Returns {@link PRICE_PLACEHOLDER} when there is no
 * value to show. Pass the site's symbol from `useSiteCurrency` rather than hard-coding one.
 */
export function formatPrice(value: number | string | null | undefined, currencySymbol: string = DEFAULT_CURRENCY_SYMBOL): string {
  const numeric = parsePrice(value);
  if (numeric == null) return PRICE_PLACEHOLDER;

  const amount = numeric.toLocaleString(undefined, {
    minimumFractionDigits: PRICE_DECIMAL_PLACES,
    maximumFractionDigits: PRICE_DECIMAL_PLACES
  });

  // Negative amounts read better as -$5.00 than $-5.00.
  return numeric < 0 ? `-${currencySymbol}${amount.replace('-', '')}` : `${currencySymbol}${amount}`;
}
